import { ref } from 'vue'
import { PushNotifications } from '@capacitor/push-notifications'
import { App } from '@capacitor/app'
import { addDeviceTokenFn, removeDeviceTokenFn } from '@/services/api/restApiCalls'
import { useAuthGlobalStore } from '@/stores/authGlobalStore'
import { useRestoGLobalStore } from '@/stores/restoGlobalStore'
import { notify } from 'notiwind'

export function useMobilePushNotification() {
  const authStore = useAuthGlobalStore()
  const restoGlobalStore = useRestoGLobalStore()
  const deviceToken = ref('')
  let appResumeListener = null
  let isPushRegistrationBlocked = false

  const isRecoverableRegistrationError = (error) => {
    const rawMessage = `${error?.error || error?.message || ''}`.toLowerCase()
    return (
      rawMessage.includes('google play services') ||
      rawMessage.includes('service_not_available') ||
      rawMessage.includes('fcm') ||
      rawMessage.includes('messaging') ||
      rawMessage.includes('network') ||
      rawMessage.includes('timeout') ||
      rawMessage.includes('unavailable')
    )
  }

  const showPushUnavailableMessage = () => {
    notify(
      {
        group: 'errormessage',
        title: 'Push notifications unavailable',
        text: 'Notifications are temporarily unavailable on this device. We will retry automatically when the app resumes.'
      },
      4000
    )
  }

  const setupResumeRetryListener = async () => {
    if (appResumeListener) return
    appResumeListener = await App.addListener('resume', async () => {
      if (!isPushRegistrationBlocked) return
      await registerNotifications()
    })
  }

  const addListeners = async () => {
    console.log('token listener registered')
    PushNotifications.addListener('registration', async (tokenData) => {
      console.log('Push registration success:', tokenData.value)
      if (authStore.isLoggedIn) {
        if (!deviceToken.value) {
          deviceToken.value = tokenData.value
          let token = await handleAddDeviceToken(tokenData.value)
        } else {
          console.log('Device token already registered on server:', deviceToken.value)
        }
      }
    })

    PushNotifications.addListener('registrationError', (err) => {
      if (isRecoverableRegistrationError(err)) {
        isPushRegistrationBlocked = true
        showPushUnavailableMessage()
        void setupResumeRetryListener()
        console.log('Push registration temporarily unavailable; waiting for app resume to retry.')
        return
      }
      console.error('Push registration error:', err?.error || err?.message || 'Unknown push registration error')
      return err.error
    })

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Notification received:', notification)
      return notification
    })

    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      console.log('Notification action performed:', notification)
      return notification
    })
  }

  const removeAllListeners = () => {
    PushNotifications.removeAllListeners()
    if (appResumeListener) {
      appResumeListener.remove()
      appResumeListener = null
    }
  }

  const registerNotifications = async () => {
    let permStatus = await PushNotifications.checkPermissions()

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions()
    }

    if (permStatus.receive !== 'granted') {
      throw new Error('User denied permissions!')
    }

    try {
      await PushNotifications.register()
      isPushRegistrationBlocked = false
      return true
    } catch (error) {
      if (isRecoverableRegistrationError(error)) {
        isPushRegistrationBlocked = true
        showPushUnavailableMessage()
        void setupResumeRetryListener()
        console.log('Push registration unavailable; retry scheduled on app resume.')
        return false
      }
      throw error
    }
  }

  // send device token
  const handleAddDeviceToken = async (token) => {
    let deviceTokenFormData = new FormData()
    deviceTokenFormData.append('device_token', token)
    try {
      const add_device_token_res = await addDeviceTokenFn(
        restoGlobalStore?.RESTO_ID,
        deviceTokenFormData
      )
      console.log('Device token sent successfully:')
    } catch (error) {
      console.error('Failed to send device token:', error)
      deviceToken.value = ''
    }
    return token
  }

  // Remove device token
  const handleRemoveDeviceToken = async () => {
    let deviceTokenFormData = new FormData()
    deviceTokenFormData.append('device_token', deviceToken.value)
    try {
      const remove_device_token_res = await removeDeviceTokenFn(
        restoGlobalStore?.RESTO_ID,
        deviceTokenFormData
      )
      console.log('Device token removed:')
    } catch (error) {
      console.error('Failed to send device token:', error)
    }
    deviceToken.value = ''
  }

  return {
    addListeners,
    registerNotifications,
    handleRemoveDeviceToken,
    removeAllListeners
  }
}
